/**
 * CreateScheduleDrawer – Right-side overlay panel for creating a new schedule.
 *
 * Matches Figma "Create Schedule" design:
 * - Select Type of Schedule (Security / Discovery / Discovery+)
 * - Schedule Details (Scan Name, Frequency, On Date / On Day, At Time)
 *   → Fields dynamically change based on selected frequency (same as old MyScans)
 * - Enter CIDR Block
 * - Cancel / Create actions
 *
 * API integration mirrors old ScheduleMain:
 *   - GET  securityscan/schedule-frequency  → load frequency + day options
 *   - POST securityscan/schedule-scan       → create schedule
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { toast } from "react-toastify";

dayjs.extend(utc);

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

type ScheduleType = "Security" | "Discovery" | "Discovery+";

interface FrequencyOption {
  id: number | string;
  name: string;
}

interface CreateScheduleDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

// ──────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────

const OnPremDiscoveryIcon: React.FC = () => (
  <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <rect x="11" y="12" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <line x1="17" y1="25" x2="17" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="23" y1="25" x2="23" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="15" y1="28" x2="25" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="21" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.3" fill="none" />
    <line x1="23" y1="20" x2="25" y2="22" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const OnPremDiscoveryPostureIcon: React.FC = () => (
  <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <rect x="11" y="12" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <line x1="17" y1="25" x2="17" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="23" y1="25" x2="23" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="15" y1="28" x2="25" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M20 15L17 16.5V19C17 20.65 18.26 22.19 20 22.7C21.74 22.19 23 20.65 23 19V16.5L20 15Z" fill="currentColor" />
  </svg>
);

const OnPremPostureIcon: React.FC = () => (
  <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M20 10L12 14V20C12 25.05 15.36 29.74 20 31C24.64 29.74 28 25.05 28 20V14L20 10Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M20 14L15 16.5V20C15 23.15 17.14 26.06 20 26.8C22.86 26.06 25 23.15 25 20V16.5L20 14Z" fill="currentColor" />
  </svg>
);

const SCHEDULE_TYPES: {
  value: ScheduleType;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "Security",
    label: "On-Prem Discovery",
    description: "Map and identify on-prem infrastructure assets.",
    icon: <OnPremDiscoveryIcon />,
  },
  {
    value: "Discovery",
    label: "On-Prem Discovery + Posture",
    description: "Internal asset mapping with compliance validation.",
    icon: <OnPremDiscoveryPostureIcon />,
  },
  {
    value: "Discovery+",
    label: "On-Prem Posture",
    description: "In-depth security analysis for existing on-prem assets.",
    icon: <OnPremPostureIcon />,
  },
];

const DRAWER_WIDTH = 650;

// ──────────────────────────────────────────────
// Helpers – time option generators
// ──────────────────────────────────────────────

/** Generate all time options in 12h format (every minute). */
const generateAllTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute++) {
      const period = hour < 12 ? "AM" : "PM";
      const h12 = hour % 12 === 0 ? 12 : hour % 12;
      const label = `${String(h12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
      options.push(label);
    }
  }
  return options;
};

/** Generate time options from now + 5 minutes onward (for today). */
const generateTimeOptionsFromNow = (): string[] => {
  const options: string[] = [];
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  const startHour = now.getHours();
  const startMinute = now.getMinutes();

  for (let hour = startHour; hour < 24; hour++) {
    for (let minute = hour === startHour ? startMinute : 0; minute < 60; minute++) {
      const period = hour < 12 ? "AM" : "PM";
      const h12 = hour % 12 === 0 ? 12 : hour % 12;
      options.push(
        `${String(h12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`
      );
    }
  }
  return options;
};

/** Convert 12h time string to UTC 24h. */
const convertToUtcTime = (time: string): string => {
  const today = dayjs().format("YYYY-MM-DD");
  return dayjs(`${today} ${time}`, "YYYY-MM-DD hh:mm A").utc().format("HH:mm");
};

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────

const CreateScheduleDrawer: React.FC<CreateScheduleDrawerProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ─── API-loaded options ────────────────────
  const [frequencyOptions, setFrequencyOptions] = useState<FrequencyOption[]>([]);
  const [dayOptions, setDayOptions] = useState<FrequencyOption[]>([]);
  const allTimeOptions = useMemo(() => generateAllTimeOptions(), []);

  // ─── Form state ────────────────────────────
  const [scheduleType, setScheduleType] = useState<ScheduleType>("Security");
  const [scanName, setScanName] = useState("");
  const [selectedFrequency, setSelectedFrequency] = useState<string | number>("");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | number>("");
  const [selectedTime, setSelectedTime] = useState("");
  const [cidrBlock, setCidrBlock] = useState("");
  const [cidrBlocks, setCidrBlocks] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Derived time options (restrict to future if date is today) ──
  const [timeOptions, setTimeOptions] = useState<string[]>(allTimeOptions);

  // ─── Load frequency options from API ───────
  useEffect(() => {
    if (!open) return;
    const loadFrequency = async () => {
      try {
        const response = await axiosInstance.get(END_POINTS.MY_SCAN_SCHEDULE_FREQUENCY);
        const data = response.data?.data;
        if (data) {
          setFrequencyOptions(data.frequency || []);
          setDayOptions(data.weekly || []);
        }
      } catch (err) {
        console.error("Error fetching schedule frequency:", err);
      }
    };
    loadFrequency();
  }, [open]);

  // ─── Frequency-dependent helpers ───────────
  const selectedFreqName = useMemo(() => {
    return frequencyOptions.find((f) => f.id === selectedFrequency)?.name || "";
  }, [selectedFrequency, frequencyOptions]);

  const isWeekly = selectedFreqName === "Weekly";
  const isHourlyOrDaily = selectedFreqName === "Hourly" || selectedFreqName === "Daily";
  /** Show date picker for frequencies other than Weekly, Hourly, Daily */
  const showDatePicker = !!selectedFrequency && !isWeekly && !isHourlyOrDaily;
  /** Show day picker only for Weekly */
  const showDayPicker = isWeekly;

  // ─── Reset form ────────────────────────────
  const resetForm = useCallback(() => {
    setScheduleType("Security");
    setScanName("");
    setSelectedFrequency("");
    setSelectedDate(null);
    setSelectedDay("");
    setSelectedTime("");
    setCidrBlock("");
    setCidrBlocks([]);
    setTimeOptions(allTimeOptions);
  }, [allTimeOptions]);

  // ─── Handlers ──────────────────────────────
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFrequencyChange = (value: string | number) => {
    setSelectedFrequency(value);
    // Reset dependent fields
    setSelectedDate(null);
    setSelectedDay("");
    setSelectedTime("");
    setTimeOptions(allTimeOptions);
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
    const isToday = newDate ? newDate.isSame(dayjs().startOf("day"), "day") : false;
    if (isToday) {
      const opts = generateTimeOptionsFromNow();
      setTimeOptions(opts);
      setSelectedTime(opts[0] || "");
    } else {
      setTimeOptions(allTimeOptions);
      setSelectedTime(allTimeOptions[0] || "");
    }
  };

  const handleCidrKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && cidrBlock.trim()) {
      e.preventDefault();
      if (!cidrBlocks.includes(cidrBlock.trim())) {
        setCidrBlocks((prev) => [...prev, cidrBlock.trim()]);
      }
      setCidrBlock("");
    }
  };

  const handleRemoveCidr = (block: string) => {
    setCidrBlocks((prev) => prev.filter((b) => b !== block));
  };

  // ─── Form validation ──────────────────────
  const isFormValid = useMemo(() => {
    if (!scanName.trim() || !selectedFrequency || !selectedTime) return false;
    if (showDatePicker && !selectedDate) return false;
    if (showDayPicker && !selectedDay) return false;
    return true;
  }, [scanName, selectedFrequency, selectedTime, selectedDate, selectedDay, showDatePicker, showDayPicker]);

  // ─── Create schedule ──────────────────────
  const handleCreate = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);

    try {
      const time24 = convertToUtcTime(selectedTime);
      let formattedDate = "";
      if (selectedDate) {
        formattedDate = `${selectedDate.date()}-${selectedDate.month() + 1}-${selectedDate.year()}`;
      }

      const isHourly = selectedFreqName === "Hourly";

      const payload: Record<string, any> = {
        schedule_name: scanName,
        frequency_id: selectedFrequency,
        time: time24,
        day: selectedDay || "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        schedule_type: scheduleType,
      };

      if (!isHourly) {
        payload.date = formattedDate;
      }

      if (cidrBlocks.length > 0) {
        payload.cidr_blocks = cidrBlocks;
      }

      // Temporary debug log: print payload unencrypted for inspection
      // Remove this log after verification
      // eslint-disable-next-line no-console
      console.log("Schedule payload (debug):", payload);

      const response = await axiosInstance.post(END_POINTS.MY_SCAN_SCHEDULED_SCAN, payload);

      if (response.data) {
        toast.success(response.data.message || "Schedule created successfully");
        onCreated?.();
        handleClose();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create schedule");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Shared styles ────────────────────────
  const cardBorder = isDark ? "#444" : "#D0D5DD";
  const selectedCardBorder = "#3A2ADA";
  const fieldBorderRadius = "8px";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.3)" } },
      }}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          borderTopLeftRadius: "12px",
          borderBottomLeftRadius: "12px",
          boxShadow: "-4px 0 30px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ─── Header ─────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${isDark ? "#333" : "#E4E7EC"}`,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "18px", color: theme.palette.text.primary }}
        >
          Create Schedule
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            backgroundColor: isDark ? "#333" : "#F2F4F7",
            "&:hover": { backgroundColor: isDark ? "#444" : "#E4E7EC" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ─── Scrollable Body ────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* ── Select Type of Schedule ───────── */}
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "14px", mb: 1.5, color: theme.palette.text.primary }}
          >
            Select Type of Schedule
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.2,
            }}
          >
            {SCHEDULE_TYPES.map((type) => {
              const isSelected = scheduleType === type.value;
              return (
                <Box
                  key={type.value}
                  onClick={() => setScheduleType(type.value)}
                  sx={{
                    border: `1.5px solid ${isSelected ? selectedCardBorder : cardBorder}`,
                    borderRadius: "14px",
                    p: 1.5,
                    minHeight: 142,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: isSelected
                      ? isDark
                        ? "rgba(58, 42, 218, 0.08)"
                        : "#F8F8FF"
                      : "transparent",
                    "&:hover": {
                      borderColor: selectedCardBorder,
                      backgroundColor: isDark ? "rgba(58, 42, 218, 0.08)" : "#F8F8FF",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: selectedCardBorder,
                      mb: 0.9,
                    }}
                  >
                    {type.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "15px",
                      lineHeight: "22px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {type.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      lineHeight: "19px",
                      color: theme.palette.text.secondary,
                      mt: 0.45,
                    }}
                  >
                    {type.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Schedule Details ──────────────── */}
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "14px", mb: 1.5, color: theme.palette.text.primary }}
          >
            Schedule Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Row 1: Scan Name + Frequency */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Scan Name"
                value={scanName}
                onChange={(e) => setScanName(e.target.value)}
                fullWidth
                size="small"
                placeholder="Enter scan name"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: fieldBorderRadius,
                  },
                }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={selectedFrequency}
                  label="Frequency"
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                  sx={{ borderRadius: fieldBorderRadius }}
                >
                  {frequencyOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Row 2: Dynamic fields based on frequency */}
            {selectedFrequency && (
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* On Date – shown for Monthly, Yearly, One‑time, etc. */}
                {showDatePicker && (
                  <FormControl fullWidth size="small">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="On Date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        disablePast
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: fieldBorderRadius,
                            "& input": {
                              height: "40px",
                              padding: "8px 14px",
                              boxSizing: "border-box",
                            },
                            "& .MuiInputAdornment-root button": {
                              outline: "none",
                              border: "none",
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </FormControl>
                )}

                {/* On Day – shown for Weekly */}
                {showDayPicker && (
                  <FormControl fullWidth size="small">
                    <InputLabel>On Day</InputLabel>
                    <Select
                      value={selectedDay}
                      label="On Day"
                      onChange={(e) => setSelectedDay(e.target.value)}
                      sx={{ borderRadius: fieldBorderRadius }}
                    >
                      {dayOptions.map((day) => (
                        <MenuItem key={day.id} value={day.id}>
                          {day.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* At Time – always shown when frequency is selected */}
                <FormControl fullWidth size="small">
                  <InputLabel>{isHourlyOrDaily ? "Start Time" : "At Time"}</InputLabel>
                  <Select
                    value={selectedTime}
                    label={isHourlyOrDaily ? "Start Time" : "At Time"}
                    onChange={(e) => setSelectedTime(e.target.value as string)}
                    sx={{ borderRadius: fieldBorderRadius }}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 250 },
                      },
                    }}
                  >
                    {timeOptions.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Enter CIDR Block ─────────────── */}
        <Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: "14px", color: theme.palette.text.primary }}
          >
            Enter CIDR Block
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            Press Enter after each CIDR block to add multiple entries.
          </Typography>

          {/* CIDR chips */}
          {cidrBlocks.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {cidrBlocks.map((block) => (
                <Chip
                  key={block}
                  label={block}
                  size="small"
                  onDelete={() => handleRemoveCidr(block)}
                  sx={{
                    borderRadius: "6px",
                    fontSize: "12px",
                    backgroundColor: isDark ? "#2E323F" : "#F2F4F7",
                  }}
                />
              ))}
            </Box>
          )}

          <TextField
            label="CIDR Block"
            placeholder="e.g. 192.168.1.0/24 → Press Enter to add"
            value={cidrBlock}
            onChange={(e) => setCidrBlock(e.target.value)}
            onKeyDown={handleCidrKeyDown}
            fullWidth
            size="small"
            multiline
            minRows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: fieldBorderRadius,
              },
            }}
          />
        </Box>
      </Box>

      {/* ─── Footer ─────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          px: 3,
          py: 2,
          borderTop: `1px solid ${isDark ? "#333" : "#E4E7EC"}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: isDark ? "#555" : "#D0D5DD",
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: isDark ? "#777" : "#98A2B3",
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isFormValid || isSubmitting}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            backgroundColor: "#3A2ADA",
            "&:hover": { backgroundColor: "#2E1FBF" },
            "&.Mui-disabled": {
              backgroundColor: isDark ? "#333" : "#E4E7EC",
              color: isDark ? "#666" : "#98A2B3",
            },
          }}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default CreateScheduleDrawer;
